import type { Workspace } from '../services/apiService'

/**
 * Remembers the last Program (workspace) used in each organization so that
 * switching organization in the header can return the user to where they
 * were. Stored next to the active-Program key in localStorage.
 */
export const LAST_WORKSPACE_BY_ORG_KEY = 'ignis_last_workspace_by_org'

function readMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LAST_WORKSPACE_BY_ORG_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function rememberWorkspaceForOrg(workspace: Pick<Workspace, 'id' | 'organizationId'>): void {
  if (!workspace.organizationId) return
  try {
    const map = readMap()
    map[workspace.organizationId] = workspace.id
    localStorage.setItem(LAST_WORKSPACE_BY_ORG_KEY, JSON.stringify(map))
  } catch {
    // ignore storage failures
  }
}

/**
 * Programs shown for the organization chosen in the header. A Program shared
 * with the user from an organization they are not a member of (so it cannot
 * be chosen in the header) stays visible in every organization rather than
 * disappearing. Pass no `memberOrgIds` to match the organization exactly.
 */
export function workspacesInOrg(
  workspaces: Workspace[],
  organizationId: string | undefined,
  memberOrgIds?: ReadonlyArray<string>,
): Workspace[] {
  if (!organizationId) return workspaces
  return workspaces.filter(workspace => workspace.organizationId === organizationId
    || (memberOrgIds !== undefined && !memberOrgIds.includes(workspace.organizationId ?? '')))
}

/**
 * The Program to open after switching to an organization: the last one used
 * there if it is still active, otherwise the first active one in the list.
 */
export function pickWorkspaceForOrg(workspaces: Workspace[], organizationId: string): Workspace | null {
  const active = workspacesInOrg(workspaces, organizationId).filter(workspace => workspace.status === 'ACTIVE')
  const lastId = readMap()[organizationId]
  return active.find(workspace => workspace.id === lastId) ?? active[0] ?? null
}
